import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { scrollToSection } from "../lib/smooth";

const SECTOR_LINKS = [
  { label: "Tech & App Architecture", target: "#sectors" },
  { label: "Advanced Manufacturing", target: "#sectors" },
  { label: "Clean Energy Systems", target: "#sectors" },
  { label: "Next-Gen Agriculture", target: "#sectors" },
  { label: "Intelligent Real Estate", target: "#sectors" },
];

const ANCHOR_LINKS = [
  { label: "Ecosystem", target: "#vision", testid: "nav-link-ecosystem" },
  { label: "Vision", target: "#insights", testid: "nav-link-vision" },
];

const Navbar = ({ onContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";
  const insightsActive = location.pathname === "/insights";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setMobileOpen(false);
    if (onHome) {
      scrollToSection(target);
    } else {
      navigate("/", { state: { scrollTo: target } });
    }
  };

  const goInsights = () => {
    setMobileOpen(false);
    if (!insightsActive) navigate("/insights");
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.7, ease: [0.33, 1, 0.68, 1] }}
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
        scrolled || !onHome
          ? "border-white/10 bg-[#0B0C10]/80 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="mx-auto flex h-[72px] max-w-[1600px] items-center justify-between px-6 md:px-12">
        <Logo />

        <nav className="hidden items-center gap-10 lg:flex" data-testid="desktop-nav">
          <div className="group relative">
            <button
              data-testid="nav-sectors-dropdown"
              onClick={() => go("#sectors")}
              className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-secondary transition-colors duration-300 hover:text-cyber"
            >
              Sectors
              <ChevronDown className="h-3 w-3 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-0 top-full pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
              <div className="w-64 border border-white/10 bg-[#121212]/95 backdrop-blur-xl">
                {SECTOR_LINKS.map((s, i) => (
                  <button
                    key={s.label}
                    data-testid={`nav-sector-link-${i}`}
                    onClick={() => go(s.target)}
                    className="flex w-full items-center justify-between border-b border-white/5 px-5 py-3.5 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-secondary transition-colors duration-300 last:border-0 hover:bg-white/5 hover:text-neon"
                  >
                    <span>{s.label}</span>
                    <span className="text-white/30">0{i + 1}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {ANCHOR_LINKS.map((link) => (
            <button
              key={link.label}
              data-testid={link.testid}
              onClick={() => go(link.target)}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary transition-colors duration-300 hover:text-cyber"
            >
              {link.label}
            </button>
          ))}

          <button
            data-testid="nav-link-insights"
            onClick={goInsights}
            className={`font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 hover:text-cyber ${
              insightsActive ? "text-cyber" : "text-secondary"
            }`}
          >
            Insights
          </button>

          <button
            data-testid="nav-initialize-button"
            onClick={onContact}
            className="group flex items-center gap-2 border border-neon/60 bg-neon/5 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-neon transition-all duration-300 hover:bg-neon hover:text-[#0B0C10] hover:drop-shadow-[0_0_12px_rgba(0,255,148,0.5)]"
          >
            Initialize Project
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0B0C10]/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-6">
              {[{ label: "Sectors", target: "#sectors" }, ...ANCHOR_LINKS].map((link, i) => (
                <button
                  key={link.label}
                  data-testid={`mobile-nav-link-${i}`}
                  onClick={() => go(link.target)}
                  className="border-b border-white/5 py-4 text-left font-mono text-xs uppercase tracking-[0.25em] text-secondary transition-colors duration-300 hover:text-cyber"
                >
                  {link.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-link-insights"
                onClick={goInsights}
                className="border-b border-white/5 py-4 text-left font-mono text-xs uppercase tracking-[0.25em] text-secondary transition-colors duration-300 hover:text-cyber"
              >
                Insights
              </button>
              <button
                data-testid="mobile-nav-initialize-button"
                onClick={() => {
                  setMobileOpen(false);
                  onContact();
                }}
                className="mt-6 flex items-center justify-center gap-2 border border-neon/60 bg-neon/5 px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-neon"
              >
                Initialize Project
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
